import { useCallback, useMemo } from 'react';
import { useDateText, useReserveDateText } from './useDateTimeText';
import {
  CAR_DRIVEN,
  CONFIRM_CAR_CONDITION,
  CONFIRM_CHECK_IN_DATE,
  CONFIRM_CHECK_IN_RESERVE,
  CONFIRM_LOANER_CAR,
  DAMAGE_DEGREE,
  DAMAGE_PART,
  HAS_DEDUCTIBLE,
  INSURANCE_USE,
  LOANER_CAR_USE,
  OTHER_PARTY,
  REPAIR_PLAN,
} from '@/consts/bodyAndPaint';
import { RepairCheckInReserveParams } from '@/types/api/bodyAndPaint';
import {
  CheckInReserveDataType,
  DamageMapItemType,
} from '@/types/bodyAndPaint';

interface CheckInReserveConfirmDataHookProp {
  initial?: CheckInReserveDataType;
}
export const useCheckInReserveConfirmData = ({
  initial,
}: CheckInReserveConfirmDataHookProp) => {
  const {
    reserveInfo,
    carConditionInfo,
    insuranceInfo,
    checkInDateInfo,
    loanerCarInfo,
  } = useMemo(() => initial ?? ({} as CheckInReserveDataType), [initial]);

  const toConvertDate = useCallback((dateText: string) => {
    const dateTextItems = dateText?.match(/\d+/g);
    const itemSize = 3;
    const [year, month, date] =
      dateTextItems && dateTextItems.length === itemSize
        ? dateTextItems
        : ['', '', ''];
    const damageLocationDateText = `${year}-${month}-${date}`;
    return new Date(damageLocationDateText);
  }, []);

  const idParams = useMemo(() => {
    return {
      m_customer_id: reserveInfo?.ids?.customerID,
      m_affiliate_id: reserveInfo?.ids?.affiliateID,
      m_unit_id: reserveInfo?.ids?.unitID,
      unit_name: reserveInfo?.ids?.unitName,
      m_plant_id: reserveInfo?.ids?.plantID,
      t_stock_car_id: reserveInfo?.ids?.stockCarID,
    };
  }, [reserveInfo]);

  const reserveInfoParams = useMemo(() => {
    return {
      car_name: reserveInfo?.carName,
      category: reserveInfo?.category,
      factory_name: reserveInfo?.factoryName,
      factory_map_address: reserveInfo?.factoryMapAddress,
      factory_phone_number: reserveInfo?.factoryPhoneNumber,
    };
  }, [reserveInfo]);

  const carConditionParams = useMemo(() => {
    return {
      can_driven: carConditionInfo?.isDrivenSelect,
      damage_parts: {
        front_side_left: Number(
          carConditionInfo?.damageMapCheck?.front_side_left ? true : false,
        ),
        front_side_center: Number(
          carConditionInfo?.damageMapCheck?.front_side_center ? true : false,
        ),
        front_side_right: Number(
          carConditionInfo?.damageMapCheck?.front_side_right ? true : false,
        ),
        front_door_left: Number(
          carConditionInfo?.damageMapCheck?.front_door_left ? true : false,
        ),
        front_door_right: Number(
          carConditionInfo?.damageMapCheck?.front_door_right ? true : false,
        ),
        rear_door_left: Number(
          carConditionInfo?.damageMapCheck?.rear_door_left ? true : false,
        ),
        rear_door_right: Number(
          carConditionInfo?.damageMapCheck?.rear_door_right ? true : false,
        ),
        rear_side_left: Number(
          carConditionInfo?.damageMapCheck?.rear_side_left ? true : false,
        ),
        rear_side_center: Number(
          carConditionInfo?.damageMapCheck?.rear_side_center ? true : false,
        ),
        rear_side_right: Number(
          carConditionInfo?.damageMapCheck?.rear_side_right ? true : false,
        ),
      },
      damage_scale: carConditionInfo?.damageDegreeSelect,
      accident_interpersonal: carConditionInfo?.otherPartSelect,
      damage_location_date: toConvertDate(carConditionInfo?.noticeDateText),
      damage_detail: carConditionInfo?.descriptionText,
    };
  }, [carConditionInfo]);

  const insuranceInfoParams = useMemo(() => {
    return {
      insurance_use_flg: insuranceInfo?.insuranceSelect ?? 0,
      m_insurance_company_id: Number(insuranceInfo?.companyID) ?? 0,
      policy_number: insuranceInfo?.certificateText ?? '',
      deductible_fee: !!insuranceInfo?.deductibleSelect
        ? !isNaN(Number(insuranceInfo?.deductibleText))
          ? Number(insuranceInfo?.deductibleText)
          : 0
        : 0,
      repair_planning: insuranceInfo?.repairPlanSelect ?? 0,
      accident_reception_number: insuranceInfo?.receiptNumberText ?? '',
    };
  }, [insuranceInfo]);

  const checkInDateParams = useMemo(() => {
    return new Date(checkInDateInfo ?? 0);
  }, [checkInDateInfo]);

  const loanerCarInfoParams = useMemo(() => {
    return {
      loaner_car_flg:
        Object.values(LOANER_CAR_USE)[loanerCarInfo?.loanerCarSelect ?? 0]
          .value,
      loaner_car_memo: loanerCarInfo?.carRequestText ?? '',
    };
  }, [loanerCarInfo]);

  const checkInReserveParams: RepairCheckInReserveParams = useMemo(
    () => ({
      ...idParams,
      data: {
        reserve_info: reserveInfoParams,
        car_condition: carConditionParams,
        insurance_info: insuranceInfoParams,
        check_in_date: checkInDateParams,
        loaner_car_info: loanerCarInfoParams,
      },
    }),
    [
      idParams,
      reserveInfoParams,
      carConditionParams,
      insuranceInfoParams,
      checkInDateParams,
      loanerCarInfoParams,
    ],
  );

  const noticeDate = useDateText({
    dateTime: toConvertDate(carConditionInfo?.noticeDateText),
  });
  const checkInDate = useReserveDateText({
    dateTime: new Date(checkInDateInfo ?? 'invalid'),
  });

  const checkInReserveLists: {
    reserveInfoList: Record<string, string>[];
    carConditionList: Record<string, string>[];
    loanerCarList: Record<string, string>[];
    checkInDateList: Record<string, string>[];
  } = useMemo(() => {
    const reserveInfoItem: Record<
      keyof typeof CONFIRM_CHECK_IN_RESERVE,
      string
    > = {
      car_name: reserveInfo?.carName ?? '-',
      category: reserveInfo?.category ?? '-',
      factory: reserveInfo?.factoryName ?? '-',
    };
    const reserveInfoList: Record<string, string>[] = Object.entries(
      reserveInfoItem,
    ).map(([key, value]) => {
      const keyName = key as keyof typeof CONFIRM_CHECK_IN_RESERVE;
      return { [CONFIRM_CHECK_IN_RESERVE[keyName]]: value };
    });

    const carDrivenList = Object.values(CAR_DRIVEN).map((item) => item.label);
    const damageDegreeList = Object.values(DAMAGE_DEGREE).map(
      (item) => item.label,
    );
    const otherPartyList = Object.values(OTHER_PARTY).map((item) => item.label);
    const insuranceUseList = Object.values(INSURANCE_USE).map(
      (item) => item.label,
    );
    const hasDeductibleList = Object.values(HAS_DEDUCTIBLE).map(
      (item) => item.label,
    );
    const repairPlanList = Object.values(REPAIR_PLAN).map((item) => item.label);
    const damagePart = Object.keys(DAMAGE_PART).reduce((list, key) => {
      const keyName = key as keyof DamageMapItemType;
      if (carConditionInfo?.damageMapCheck[keyName]) {
        list.push(DAMAGE_PART[keyName]);
      }
      return list;
    }, [] as string[]);

    const confirmCarConditionItem: Record<
      keyof typeof CONFIRM_CAR_CONDITION,
      string
    > = {
      car_drive: carDrivenList.at(carConditionInfo?.isDrivenSelect) ?? '-',
      damage_part: (damagePart ?? ['-']).join(' '),
      notice_date: noticeDate ? `${noticeDate}から` : '-',
      damage_degree:
        damageDegreeList.at(carConditionInfo?.damageDegreeSelect) ?? '-',
      has_other_party:
        otherPartyList.at(carConditionInfo?.otherPartSelect) ?? '-',
      description: carConditionInfo?.descriptionText ?? '-',

      use_insurance:
        insuranceUseList.at(insuranceInfo?.insuranceSelect ?? -1) ?? '-',
      company_name: insuranceInfo?.companyPick ?? '-',
      certificate_number: insuranceInfo?.certificateText ?? '-',
      receipt_number: insuranceInfo?.receiptNumberText ?? '-',
      deductible:
        hasDeductibleList.at(insuranceInfo?.deductibleSelect ?? -1) ?? '-',
      repair_plan:
        repairPlanList.at(insuranceInfo?.repairPlanSelect ?? -1) ?? '-',
    };
    const confirmCarConditionList: Record<string, string>[] = Object.entries(
      confirmCarConditionItem,
    ).map(([key, value]) => {
      const keyName = key as keyof typeof CONFIRM_CAR_CONDITION;
      return { [CONFIRM_CAR_CONDITION[keyName]]: value };
    });

    const loanerCarItem: Record<keyof typeof CONFIRM_LOANER_CAR, string> = {
      use_loaner_car: loanerCarInfo?.loanerCarSelect
        ? Object.values(LOANER_CAR_USE)
            .map((item) => item.label)
            .at(loanerCarInfo?.loanerCarSelect) ?? '-'
        : '-',
    };
    const loanerCarList: Record<string, string>[] = Object.entries(
      loanerCarItem,
    ).map(([key, value]) => {
      const keyName = key as keyof typeof CONFIRM_LOANER_CAR;
      return { [CONFIRM_LOANER_CAR[keyName]]: value };
    });

    const checkInItem: Record<keyof typeof CONFIRM_CHECK_IN_DATE, string> = {
      check_in_date: checkInDate ? `${checkInDate}〜` : '-',
    };
    const checkInList: Record<string, string>[] = Object.entries(
      checkInItem,
    ).map(([key, value]) => {
      const keyName = key as keyof typeof CONFIRM_CHECK_IN_DATE;
      return { [CONFIRM_CHECK_IN_DATE[keyName]]: value };
    });
    return {
      reserveInfoList: reserveInfoList,
      carConditionList: confirmCarConditionList,
      loanerCarList: loanerCarList,
      checkInDateList: checkInList,
    };
  }, [
    carConditionInfo,
    checkInDate,
    insuranceInfo,
    noticeDate,
    reserveInfo,
    loanerCarInfo,
  ]);
  return { checkInReserveParams, checkInReserveLists };
};
